import {useMutation, UseMutationResult, useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {PRESENTATIONS_LIST_PAGE_SIZE_DEFAULT_VALUES} from '../../../constants';
import {useListParamsState} from '../../../hooks/useListParamsState';
import {createDataViewListStateProps} from '../../../hooks/listParams';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {IPresentation, IPresentationMeta} from '../../../incase-domain/presentation';
import {fetchOwnerSharedPresentationsPaginated, removeSharedPresentation, updateSharedPresentation} from '../../../service/presentation';
import {IUpdateError} from '../../../store/actions/types/error.types';
import PresentationListButtons, {BUTTON_OPTIONS} from '../../../ui/components/presentationList/PresentationListButtons';
import DataView, {ViewMode} from '../../../ui/components/common/data/DataView';
import {PaginationPosition} from '../../../ui/components/common/data/pagination/Paginator';

interface PresentationSharedPresentationsSettingsFormProps {
    presentation: IPresentationMeta;
}

export default function PresentationSharedPresentationsSettingsForm({
    presentation,
}: PresentationSharedPresentationsSettingsFormProps): JSX.Element {
    const {t} = useTranslation();
    const client = useQueryClient();

    const [deletePresentations, setDeletePresentations] = useState(false);
    const [presentationToDelete, setPresentationToDelete] = useState<IPresentation[]>([]);

    const {listParams, setListParams} = useListParamsState({
        size: PRESENTATIONS_LIST_PAGE_SIZE_DEFAULT_VALUES.initialPageSize,
    });
    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const sharedPresentationList = useQuery(
        QUERY_KEYS.sharedPresentation.list({listParams}),
        () => fetchOwnerSharedPresentationsPaginated(presentation._id, listParams.page, listParams.size),
        {keepPreviousData: true}
    );

    const deleteSharedPresentation: UseMutationResult<IPresentation & {errors: Record<string, string[]>}, IUpdateError, IPresentation> =
        useMutation((presentation) => removeSharedPresentation(presentation._id, presentation.userEmail as string), {
            onSuccess: () => {
                client.invalidateQueries({queryKey: QUERY_KEYS.sharedPresentation.lists()});
                setPresentationToDelete([]);
                toast.success(t('presEditPage.settings.sharedPresentations.toast.successfullyDeletedSharedPresUser'));
            },
            onError: () => {
                toast.error(t('presEditPage.settings.sharedPresentations.toast.unsuccessfullyDeletedSharedPresUser'));
            },
        });

    const updatePresentation: UseMutationResult<void, IUpdateError, IPresentation> = useMutation(
        (presentation) => updateSharedPresentation(presentation._id, presentation.userEmail as string),
        {
            onSuccess: () => {
                client.invalidateQueries({queryKey: QUERY_KEYS.sharedPresentation.lists()});
                toast.success(t('presEditPage.settings.sharedPresentations.toast.successfullyUpdateReadOnly'));
            },
            onError: () => {
                toast.success(t('presEditPage.settings.sharedPresentations.toast.unsuccessfullyUpdateReadOnly'));
            },
        }
    );

    function renderCollaborateUserEmail(row: IPresentation): JSX.Element {
        return <>{row.userEmail}</>;
    }

    function renderActionButtons(row: IPresentation): JSX.Element {
        return (
            <PresentationListButtons
                presentation={row}
                setDeletePresentations={setDeletePresentations}
                setPresentationToDelete={setPresentationToDelete}
                buttonOptions={[BUTTON_OPTIONS.READ_ONLY, BUTTON_OPTIONS.DELETE_PRESENTATION]}
                updateSharedPresentation={() => updatePresentation.mutate(row)}
            />
        );
    }

    return (
        <>
            <DataView
                views={[
                    {
                        columns: [
                            {
                                body: (row) => renderCollaborateUserEmail(row),
                                width: '180%',
                                label: t('connections.user'),
                            },
                            {
                                body: (row) => renderActionButtons(row),
                            },
                        ],
                        view: ViewMode.TABLE,
                    },
                ]}
                rows={sharedPresentationList?.data?.content || []}
                rowIdentifier={(row) => row._id}
                loading={sharedPresentationList.isLoading || sharedPresentationList.isFetching}
                paginationData={{
                    pageSize: listParams.size,
                    currentPage: listParams.page,
                    itemsCount: sharedPresentationList.data?.numberOfElements || 0,
                }}
                {...dataViewListState}
                hideToolbar
                paginationPosition={PaginationPosition.BOTTOM}
            />
            <ConfirmModal
                message={`${t('editorPage.deletePresentationModal.deletePresentationConfirmation')} ${presentationToDelete[0]?.userEmail}?`}
                color="error"
                isOpen={deletePresentations}
                onCancel={() => {
                    setPresentationToDelete([]);
                    setDeletePresentations(false);
                }}
                onConfirm={() => {
                    deleteSharedPresentation.mutate(presentationToDelete[0]);
                    setDeletePresentations(false);
                }}
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
            />
        </>
    );
}
